import React, { createContext, useContext, ReactNode } from "react";
import {
  useMsal,
  useIsAuthenticated,
  useAccount,
} from "@azure/msal-react";
import { loginRequest, apiScopes } from "./authConfig";

interface AuthContextType {
  isAuthenticated: boolean;
  account: any;
  login: () => Promise<any>;
  logout: () => void;
  getAccessToken: (forceRefresh?: boolean) => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount();

  // Get account from accounts array if useAccount returns null
  const activeAccount = account || accounts[0] || null;

  // Debug logging
  React.useEffect(() => {
    console.log('AuthProvider - accounts array:', accounts);
    console.log('AuthProvider - useAccount():', account);
    console.log('AuthProvider - activeAccount:', activeAccount);
    if (activeAccount) {
      console.log('ActiveAccount properties:', Object.keys(activeAccount));
      console.log('ActiveAccount name:', activeAccount.name);
      console.log('ActiveAccount username:', activeAccount.username);
      console.log('ActiveAccount idTokenClaims:', activeAccount.idTokenClaims);
    }
  }, [accounts, account, activeAccount]);

  const login = async () => {
    try {
      // Always use OIDC scopes for authentication - simple and reliable
      // Access tokens for API calls are acquired separately in getAccessToken()
      await instance.loginRedirect(loginRequest);
      // Note: loginRedirect doesn't return a response immediately
      // The redirect will happen and the app will reload with authentication
    } catch (error: any) {
      console.error("Login failed:", error);
      throw error;
    }
  };

  const logout = () => {
    instance.logoutRedirect({
      postLogoutRedirectUri: `${window.location.origin}/`,
    });
  };

  const getAccessToken = async (forceRefresh = false): Promise<string | null> => {
    const currentAccount = activeAccount;
    if (!currentAccount) {
      console.warn('getAccessToken: No active account available');
      return null;
    }

    // Use API scopes to get access tokens for backend API calls
    // These are different from login scopes (OIDC scopes are for authentication only)
    for (const scope of apiScopes) {
      try {
        console.log('getAccessToken: Acquiring token (forceRefresh:', forceRefresh, ', scope:', scope, ') for account:', currentAccount.username);
        const response = await instance.acquireTokenSilent({
          scopes: [scope],
          account: currentAccount,
          forceRefresh,
        });
        console.log('getAccessToken: Token acquired successfully with scope:', scope);
        return response.accessToken;
      } catch (error: any) {
        console.warn(`Failed to acquire token with scope ${scope}:`, error.errorCode || error.message);
        
        // If this is an invalid_client or AADSTS650057 error, try next scope format
        if (error.errorCode === 'invalid_client' || 
            error.errorCode === 'invalid_request' ||
            error.message?.includes('AADSTS650057') ||
            error.errorMessage?.includes('AADSTS650057')) {
          continue; // Try next scope format
        }
        
        // For interaction_required or consent_required, try redirect with current scope
        if (error.errorCode === 'interaction_required' || error.errorCode === 'consent_required') {
          try {
            console.log('getAccessToken: Attempting redirect due to interaction required');
            await instance.acquireTokenRedirect({
              scopes: [scope],
              account: currentAccount,
            });
            return null;
          } catch (redirectError: any) {
            console.error("Failed to acquire token via redirect:", redirectError);
            // If redirect also fails with AADSTS650057, try next scope
            if (redirectError?.errorCode === 'invalid_client' || 
                redirectError?.message?.includes('AADSTS650057')) {
              continue;
            }
            return null;
          }
        }
      }
    }
    
    console.error('getAccessToken: All API scope formats failed');
    return null;
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        account: activeAccount,
        login,
        logout,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

